import {
    BuildingOffice2Icon,
    EnvelopeIcon,
    PhoneIcon,
} from "@heroicons/react/24/outline";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "@inertiajs/react";
import toast from "react-hot-toast";
import AppLayout from "../../layouts/appLayout";
import Button from "../../components/common/buttons/button";
import { useState } from "react";

interface ContactInterface {
    data: {
        heading: string;
        subtitle: string;
        address: string;
        number: string;
        email: string;
    };
    title: string;
}

export default function Contact({ data, title }: ContactInterface) {
    const [loading, setLoading] = useState(false);
    const intl = useIntl();
    const {
        data: formData,
        setData,
        post,
        errors,
        reset,
    } = useForm({ name: "", email: "", message: "", terms: false as boolean });
    const submit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        post(route("contact.submit"), {
            onStart: () => {
                setLoading(true);
            },
            onSuccess: () => {
                reset();
                toast.success(
                    intl.formatMessage(
                        {
                            id: "Thanks, your message has been sent to :name.",
                        },
                        { name: "the Voice Team" },
                    ),
                );
            },
            onFinish: () => {
                setLoading(false);
            },
        });
    };
    return (
        <AppLayout title={title}>
            <div className="mx-auto max-w-7xl py-12 px-4 sm:px-6 lg:px-8 lg:py-36">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-12">
                    <div className="sm:col-span-6 flex flex-col space-y-6">
                        <h1 className="font-hero text-3xl sm:text-5xl md:text-6xl">
                            {data.heading}
                        </h1>
                        <p className="text-lg text-gray-900">{data.subtitle}</p>
                        <dl className="mt-10 space-y-4 text-base leading-7 text-gray-900">
                            {data.address && (
                                <div className="flex gap-x-4">
                                    <dt className="flex-none">
                                        <span className="sr-only">
                                            <FormattedMessage id="Address" />
                                        </span>
                                        <BuildingOffice2Icon className="h-7 w-6 text-gray-700" />
                                    </dt>
                                    <dd className="whitespace-pre-line">
                                        {data.address}
                                    </dd>
                                </div>
                            )}
                            {data.number && (
                                <div className="flex gap-x-4">
                                    <dt className="flex-none">
                                        <span className="sr-only">
                                            <FormattedMessage id="Telephone" />
                                        </span>
                                        <PhoneIcon className="h-7 w-6 text-gray-700" />
                                    </dt>
                                    <dd>
                                        <a
                                            className="underline underline-offset-4"
                                            href={`tel:${data.number}`}
                                        >
                                            {data.number}
                                        </a>
                                    </dd>
                                </div>
                            )}
                            {data.email && (
                                <div className="flex gap-x-4">
                                    <dt className="flex-none">
                                        <span className="sr-only">
                                            <FormattedMessage id="Email" />
                                        </span>
                                        <EnvelopeIcon className="h-7 w-6 text-gray-700" />
                                    </dt>
                                    <dd>
                                        <a
                                            className="underline underline-offset-4"
                                            href={`mailto:${data.email}`}
                                        >
                                            {data.email}
                                        </a>
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>
                    <div className="sm:col-span-6">
                        <form className="space-y-6" onSubmit={submit}>
                            {/* Name */}
                            <div>
                                <label
                                    htmlFor="name"
                                    className="text-base font-medium text-gray-900"
                                >
                                    <FormattedMessage id="Name" />
                                    <span className="ml-1">*</span>
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="name"
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) =>
                                            setData("name", e.target.value)
                                        }
                                        className="shadow-xs focus:ring-gray-500 focus:border-gray-500 mt-1 block w-full sm:text-sm border border-gray-300 rounded-md"
                                    />
                                </div>
                                {errors.name && (
                                    <div
                                        className="mt-1 text-sm text-red-600 flex"
                                        id="name-error"
                                    >
                                        {errors.name}
                                    </div>
                                )}
                            </div>
                            {/* Email */}
                            <div>
                                <label
                                    htmlFor="email"
                                    className="text-base font-medium text-gray-900"
                                >
                                    <FormattedMessage id="Email" />
                                    <span className="ml-1">*</span>
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="email"
                                        type="email"
                                        onChange={(e) =>
                                            setData("email", e.target.value)
                                        }
                                        value={formData.email}
                                        className="shadow-xs focus:ring-gray-500 focus:border-gray-500 mt-1 block w-full sm:text-sm border border-gray-300 rounded-md"
                                    />
                                </div>
                                {errors.email && (
                                    <div
                                        className="mt-1 text-sm text-red-600 flex"
                                        id="email-error"
                                    >
                                        {errors.email}
                                    </div>
                                )}
                            </div>
                            {/* Message */}
                            <div>
                                <label
                                    htmlFor="message"
                                    className="text-base font-medium text-gray-900"
                                >
                                    <FormattedMessage id="Message" />
                                    <span className="ml-1">*</span>
                                </label>
                                <div className="mt-1">
                                    <textarea
                                        id="message"
                                        value={formData.message}
                                        onChange={(e) =>
                                            setData("message", e.target.value)
                                        }
                                        maxLength={500}
                                        rows={6}
                                        className="shadow-xs focus:ring-gray-500 focus:border-gray-500 mt-1 block w-full sm:text-sm border border-gray-300 rounded-md"
                                    ></textarea>
                                </div>
                                <div className="grid grid-cols-1 gap-y-5 gap-x-4 sm:grid-cols-6 mt-1">
                                    <div className="sm:col-span-3">
                                        {errors.message && (
                                            <div
                                                className="text-sm text-red-600 flex"
                                                id="message-error"
                                            >
                                                {errors.message}
                                            </div>
                                        )}
                                    </div>
                                    <div className="sm:col-span-3 text-right">
                                        <p className="text-sm text-gray-500">
                                            <span>
                                                {500 - formData.message.length}
                                            </span>{" "}
                                            <FormattedMessage id="characters left" />
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {/* Terms */}
                            <div className="mt-1">
                                <fieldset>
                                    <legend className="sr-only">
                                        <FormattedMessage id="Terms &amp; Conditions" />
                                    </legend>
                                    <div className="relative flex items-start">
                                        <div className="flex h-5 items-center">
                                            <input
                                                onChange={(e) =>
                                                    setData(
                                                        "terms",
                                                        e.target.checked,
                                                    )
                                                }
                                                id="terms"
                                                aria-describedby="terms-description"
                                                name="terms"
                                                type="checkbox"
                                                checked={formData.terms}
                                                className="h-4 w-4 rounded-sm border-gray-300 text-gray-600 focus:ring-gray-500"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label
                                                htmlFor="terms"
                                                className="font-medium text-gray-700"
                                            >
                                                <FormattedMessage id="I agree to Voice's Terms of Service and" />{" "}
                                                <a
                                                    href={route(
                                                        "privacy-policy",
                                                    )}
                                                    className="underline underline-offset-4"
                                                >
                                                    <FormattedMessage id="Privacy Policy" />
                                                </a>
                                            </label>
                                        </div>
                                    </div>
                                    {errors.terms && (
                                        <div
                                            className="ml-7 mt-1 text-sm text-red-600 flex"
                                            id="terms-error"
                                        >
                                            {errors.terms}
                                        </div>
                                    )}
                                </fieldset>
                            </div>
                            <div className="flex flex-row-reverse">
                                <Button
                                    type="submit"
                                    color="black"
                                    loading={loading}
                                    className="w-40 justify-center h-12"
                                >
                                    <FormattedMessage id="Send message" />
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
